import React from 'react';
import styled from 'styled-components';
import { useAppState } from '../../../store/appstate';
import usePermissions from '../../../services/hooks/usePermissions';
import sessionActions from '../../../store/session/actions';
import { SessionState } from '../../../store/session/sessionSlice';
import { useAppDispatch } from '../../../store';
import SearchSelect from '../../atoms/Input/SearchSelect';
import { useOrganizationsStore } from '../../../store/admin/organizations/organizationsStore';
import { config } from '../../../config';

type SelectUserType = {
    value: string;
    label: string;
};

interface SelectOrganizationProps {
    includeAllOption?: boolean;
    value?: string;
    onChange?: (orgId: string) => void;
}

const SelectOrganization: React.FC<SelectOrganizationProps> = ({
    includeAllOption,
    value: controlledValue,
    onChange: onChangeProp,
}) => {
    const dispatch = useAppDispatch();

    const { user, selectedOrganization, selectedOrganizationPending } = useAppState<SessionState>(
        s => s.session
    );
    const { organizations } = useOrganizationsStore();
    const { hasRead } = usePermissions(['usermanagement-all-organizationlist']);

    const handleSelectChange = (selected: string) => {
        if (onChangeProp) {
            onChangeProp(selected);
        } else {
            dispatch(sessionActions.setSelectedOrganization(selected));
        }
    };

    const internalOrganizationId = config.InternalOrganization;
    const internalOrganization = organizations.find(org => org.id === internalOrganizationId);

    const sortedOrganizations = organizations
        .filter(org => org.id !== internalOrganizationId)
        .sort((a, b) => a.name.localeCompare(b.name));

    const options: SelectUserType[] = [];

    if (includeAllOption) {
        options.push({ value: 'all', label: 'All organizations' });
    }

    options.push({
        value: internalOrganizationId,
        label: internalOrganization?.name ?? user?.organization?.name ?? 'Internal organization',
    });

    options.push(...sortedOrganizations.map(x => ({ value: x.id, label: x.name })));

    const selectedValue = controlledValue ?? (selectedOrganization ? selectedOrganization.id : '');

    if (!hasRead || !user?.isInternal) return <></>;

    return (
        <SelectStoreWrapper>
            <SearchSelect
                dataTestId="select-organization"
                disabled={organizations.length <= 1 || selectedOrganizationPending}
                placeholder="Select Organization"
                value={selectedValue}
                defaultValue={selectedValue}
                options={options}
                onChange={s => handleSelectChange(s)}
            />
        </SelectStoreWrapper>
    );
};

const SelectStoreWrapper = styled.div`
    width: 20rem;
    justify-content: flex-end;
    margin-left: auto;
    align-self: center;
`;

export default SelectOrganization;
